import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS, getCurrentBrand } from '../config/api';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  useTheme,
  Fade,
  Skeleton,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Avatar
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  ShowChart as LineChartIcon,
  PieChart as PieChartIcon,
  Timeline as AreaChartIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  Search as SearchIcon,
  WhatsApp as WhatsAppIcon,
  Google as GoogleIcon,
  ListAlt as ListAltIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { TextField, Button, Stack } from '@mui/material';

// Custom TikTok icon using SVG
const TikTokIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

// XHS icon using image
const XHSIcon = () => (
  <Box
    component="img"
    src={process.env.PUBLIC_URL + '/xhs.png'}
    alt="小红书"
    sx={{
      width: '80%',
      height: '80%',
      objectFit: 'contain',
      display: 'block',
      minWidth: '32px',
      minHeight: '32px'
    }}
    onError={(e) => {
      console.error('Failed to load XHS image:', e.target.src);
    }}
  />
);



// Professional gradient definitions for enhanced visual appeal
const GRADIENTS = {
  primary: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 50%, #1e40af 100%)',
  secondary: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 50%, #6d28d9 100%)',
  success: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
  warning: 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #b45309 100%)',
  error: 'linear-gradient(135deg, #ef4444 0%, #dc2626 50%, #b91c1c 100%)',
  info: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #0e7490 100%)',
  tiktok: 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #333333 100%)',
  xhs: 'linear-gradient(135deg, #ff2442 0%, #e11d48 50%, #be123c 100%)',
  facebook: 'linear-gradient(135deg, #1877f2 0%, #0d6efd 50%, #0a58ca 100%)',
  instagram: 'linear-gradient(135deg, #e4405f 0%, #c13584 50%, #a21caf 100%)'
};

const DashboardOverview = ({ stats, startDate, endDate, onDataUpdate, onDateChange, onRefresh, isLoading }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartType, setChartType] = useState('bar');
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const theme = useTheme();
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedPlatformDetail, setSelectedPlatformDetail] = useState(null);
  const [detailEvents, setDetailEvents] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);


  // Auto-switch chart type if pie chart is selected but not available
  useEffect(() => {
    if (chartType === 'pie' && selectedPlatform !== 'all') {
      setChartType('bar');
    }
  }, [selectedPlatform, chartType]);

  // Sync temp dates when props change
  useEffect(() => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
  }, [startDate, endDate]);

  // Handle search button click
  const handleSearch = () => {
    if (tempStartDate && tempEndDate) {
      // Normalize dates: set start to beginning of day, end to end of day
      const normalizedStart = new Date(tempStartDate);
      normalizedStart.setHours(0, 0, 0, 0);

      const normalizedEnd = new Date(tempEndDate);
      normalizedEnd.setHours(23, 59, 59, 999);

      onDateChange(normalizedStart, normalizedEnd);
    }
  };

  const getTotalShares = () => {
    // Calculate total actions from the actual analytics data
    return analyticsData.reduce((sum, item) => {
      const totalActions = parseInt(item.total_actions) || 0;
      return sum + totalActions;
    }, 0);
  };

  const getPlatformPercentage = (platformShares) => {
    const total = getTotalShares();
    return total > 0 ? (platformShares / total) * 100 : 0;
  };

  const getFilteredChartData = () => {
    if (selectedPlatform === 'all') {
      return data.map((item) => ({
        month: item.month,
        total: (item.tiktok || 0) + (item.xhs || 0) + (item.facebook || 0) + (item.instagram || 0),
      }));
    }

    return data.map(item => ({
      month: item.month,
      [selectedPlatform]: item[selectedPlatform]
    }));
  };

  const getYAxisTicks = () => {
    if (selectedPlatform === 'all') {
      // For "all" platform, calculate ticks based on total data range
      const chartData = getFilteredChartData();
      const totalValues = chartData.map(item => item.total);
      const maxValue = Math.max(...totalValues);

      // If no data or max is 0, return simple ticks
      if (maxValue === 0) {
        return [0, 1, 2, 3, 4, 5];
      }

      // Create dynamic ticks based on the maximum value
      const niceMax = Math.ceil(maxValue * 1.2); // Add 20% padding

      // Determine appropriate tick interval based on max value
      let tickInterval;
      if (niceMax <= 10) {
        tickInterval = 2;
      } else if (niceMax <= 20) {
        tickInterval = 5;
      } else if (niceMax <= 50) {
        tickInterval = 10;
      } else if (niceMax <= 100) {
        tickInterval = 20;
      } else if (niceMax <= 200) {
        tickInterval = 50;
      } else if (niceMax <= 500) {
        tickInterval = 100;
      } else {
        tickInterval = Math.ceil(niceMax / 5);
      }

      const ticks = [];
      for (let i = 0; i <= niceMax; i += tickInterval) {
        ticks.push(i);
      }

      // Ensure we have at least 4 ticks
      if (ticks.length < 4) {
        const additionalTicks = Math.ceil(niceMax / 3);
        return [0, additionalTicks, additionalTicks * 2, additionalTicks * 3];
      }

      return ticks;
    } else {
      // For individual platforms, calculate dynamic ticks based on actual data
      const chartData = getFilteredChartData();
      const platformData = chartData.map(item => item[selectedPlatform] || 0);
      const maxValue = Math.max(...platformData);

      // If no data or max is 0, return simple ticks
      if (maxValue === 0) {
        return [0, 1, 2, 3, 4, 5];
      }

      // Create dynamic ticks based on the maximum value
      const niceMax = Math.ceil(maxValue * 1.2); // Add 20% padding

      // Determine appropriate tick interval based on max value
      let tickInterval;
      if (niceMax <= 10) {
        tickInterval = 2;
      } else if (niceMax <= 20) {
        tickInterval = 5;
      } else if (niceMax <= 50) {
        tickInterval = 10;
      } else if (niceMax <= 100) {
        tickInterval = 20;
      } else if (niceMax <= 200) {
        tickInterval = 50;
      } else {
        tickInterval = Math.ceil(niceMax / 5);
      }

      const ticks = [];
      for (let i = 0; i <= niceMax; i += tickInterval) {
        ticks.push(i);
      }

      // Ensure we have at least 4 ticks
      if (ticks.length < 4) {
        const additionalTicks = Math.ceil(niceMax / 3);
        return [0, additionalTicks, additionalTicks * 2, additionalTicks * 3];
      }

      return ticks;
    }
  };

  // Use stats prop from App.js instead of fetching separately
  // Wrap in useMemo to prevent unnecessary recalculations
  const analyticsData = useMemo(() => {
    return Array.isArray(stats) ? stats : [];
  }, [stats]);

  // Fetch monthly data when date range changes (summary is already fetched in App.js)
  useEffect(() => {
    let isCancelled = false; // Flag to prevent state updates if component unmounts

    const fetchMonthlyData = async () => {
      try {
        // Send dates as date strings (YYYY-MM-DD) in Malaysia time to avoid UTC conversion
        const startDateStr = startDate.toLocaleDateString('en-CA', { timeZone: 'Asia/Kuala_Lumpur' }); // en-CA gives YYYY-MM-DD
        const endDateStr = endDate.toLocaleDateString('en-CA', { timeZone: 'Asia/Kuala_Lumpur' });

        // Fetch monthly data for charts
        if (!isCancelled) {
          setLoading(true);
          const monthlyResponse = await fetch(`${API_ENDPOINTS.ANALYTICS_MONTHLY}?startDate=${startDateStr}&endDate=${endDateStr}&brand=${getCurrentBrand()}`);

          if (monthlyResponse.ok && !isCancelled) {
            const monthlyData = await monthlyResponse.json();

            // Transform the data to match the chart format
            const chartData = monthlyData.reduce((acc, item) => {
              const monthKey = new Date(item.month).toLocaleDateString('en-US', { month: 'short' });
              const existingMonth = acc.find(m => m.month === monthKey);

              if (existingMonth) {
                existingMonth[item.platform] = parseInt(item.total_actions) || 0;
              } else {
                acc.push({
                  month: monthKey,
                  [item.platform]: parseInt(item.total_actions) || 0
                });
              }

              return acc;
            }, []);

            // If no data, create a single month entry with 0 values for all platforms
            if (chartData.length === 0) {
              chartData.push({
                month: new Date().toLocaleDateString('en-US', { month: 'short' }),
                instagram: 0,
                facebook: 0,
                tiktok: 0,
                xhs: 0
              });
            }

            setData(chartData);
          } else if (!isCancelled) {
            // Fallback to generated data if API fails
            const chartData = generateMonthlyData();
            setData(chartData);
          }
        }
      } catch (error) {
        if (!isCancelled) {
          console.error('Error fetching monthly data:', error);
          // Fallback to generated data if API fails
          const chartData = generateMonthlyData();
          setData(chartData);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchMonthlyData();

    // Cleanup function to prevent state updates if component unmounts
    return () => {
      isCancelled = true;
    };
  }, [startDate, endDate]);

  const platformData = useMemo(() => {
    // Define all platforms that should always be shown
    const lemon8Enabled = ['connectai', 'ashley'].includes(String(getCurrentBrand() || '').toLowerCase());
    const allPlatforms = lemon8Enabled
      ? ['instagram', 'facebook', 'tiktok', 'xhs', 'whatsapp', 'google', 'lemon8']
      : ['instagram', 'facebook', 'tiktok', 'xhs', 'whatsapp', 'google'];

    // Map analytics data to platform cards
    const platformMap = {
      'tiktok': {
        title: 'TikTok',
        subtitle: 'Video',
        icon: TikTokIcon,
        color: '#000000',
        chartColor: '#00f2ea',
        gradient: GRADIENTS.tiktok,
        accent: '#00f2ea',
        shadow: '0 20px 60px rgba(0, 242, 234, 0.3)',
        glow: '0 0 30px rgba(0, 242, 234, 0.4)'
      },
      'xhs': {
        title: '小红书',
        subtitle: 'Lifestyle',
        icon: XHSIcon,
        color: '#ff2442',
        chartColor: '#ff6b9d',
        gradient: GRADIENTS.xhs,
        accent: '#ffd700',
        shadow: '0 20px 60px rgba(255, 36, 66, 0.3)',
        glow: '0 0 30px rgba(255, 36, 66, 0.4)'
      },
      'facebook': {
        title: 'Facebook',
        subtitle: 'Social',
        icon: FacebookIcon,
        color: '#1877f2',
        chartColor: '#4267B2',
        gradient: GRADIENTS.facebook,
        accent: '#42a5f5',
        shadow: '0 20px 60px rgba(24, 119, 242, 0.3)',
        glow: '0 0 30px rgba(24, 119, 242, 0.4)'
      },
      'instagram': {
        title: 'Instagram',
        subtitle: 'Visual',
        icon: InstagramIcon,
        color: '#e4405f',
        chartColor: '#ff6b35',
        gradient: GRADIENTS.instagram,
        accent: '#ff9800',
        shadow: '0 20px 60px rgba(228, 64, 95, 0.3)',
        glow: '0 0 30px rgba(228, 64, 95, 0.4)'
      },
      'whatsapp': {
        title: 'WhatsApp',
        subtitle: 'Messaging',
        icon: WhatsAppIcon,
        color: '#25d366',
        chartColor: '#25d366',
        gradient: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
        accent: '#25d366',
        shadow: '0 20px 60px rgba(37, 211, 102, 0.3)',
        glow: '0 0 30px rgba(37, 211, 102, 0.4)'
      },
      'google': {
        title: 'Google',
        subtitle: 'Search',
        icon: GoogleIcon,
        color: '#fbbc05',
        chartColor: '#fbbc05',
        gradient: 'linear-gradient(135deg, #fbbc05 0%, #ff9800 100%)',
        accent: '#fbbc05',
        shadow: '0 20px 60px rgba(251, 188, 5, 0.3)',
        glow: '0 0 30px rgba(251, 188, 5, 0.4)'
      },
      'lemon8': {
        title: 'Lemon8',
        subtitle: 'Lifestyle',
        icon: AutoAwesomeIcon,
        color: '#f59e0b',
        chartColor: '#f59e0b',
        gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #b45309 100%)',
        accent: '#fde68a',
        shadow: '0 20px 60px rgba(245, 158, 11, 0.3)',
        glow: '0 0 30px rgba(245, 158, 11, 0.35)'
      }
    };

    // Create a map of analytics data for quick lookup
    const analyticsMap = {};
    analyticsData.forEach(item => {
      analyticsMap[item.platform] = item;
    });

    // Always return all platforms, with 0 values if no data
    return allPlatforms.map(platformName => {
      const platform = platformMap[platformName];
      const analyticsItem = analyticsMap[platformName];
      const shares = analyticsItem ? (parseInt(analyticsItem.shares) || 0) : 0;
      const totalActions = analyticsItem ? (parseInt(analyticsItem.total_actions) || 0) : 0;

      return {
        key: platformName,
        ...platform,
        value: totalActions,
        shares,
        clicks: analyticsItem ? (parseInt(analyticsItem.clicks) || 0) : 0,
        follows: analyticsItem ? (parseInt(analyticsItem.follows) || 0) : 0,
        engagements: analyticsItem ? (parseInt(analyticsItem.engagements) || 0) : 0,
        lastAction: analyticsItem?.last_action || null,
        totalActions,
        growth: '+0.0%', // You can calculate this based on historical data
        trend: 'up'
      };
    });
  }, [analyticsData]);

  const generateMonthlyData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const baseValues = {
      tiktok: [320, 380, 420, 480, 520, 580, 720, 890, 760, 680, 540, 450],
      xhs: [280, 320, 380, 420, 480, 520, 680, 820, 720, 640, 480, 380],
      facebook: [200, 240, 280, 320, 380, 420, 520, 680, 620, 560, 420, 320],
      instagram: [180, 220, 260, 300, 360, 400, 480, 620, 580, 520, 380, 280]
    };

    return months.map((month, index) => ({
      month,
      tiktok: baseValues.tiktok[index] + Math.floor(Math.random() * 100) - 50,
      xhs: baseValues.xhs[index] + Math.floor(Math.random() * 80) - 40,
      facebook: baseValues.facebook[index] + Math.floor(Math.random() * 60) - 30,
      instagram: baseValues.instagram[index] + Math.floor(Math.random() * 50) - 25
    }));
  };


  useEffect(() => {
    if (onDataUpdate && data.length > 0) {
      onDataUpdate(data);
    }
  }, [data, onDataUpdate]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Card
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            backdropFilter: 'blur(20px)',
            p: 2,
            minWidth: 200
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: theme.palette.text.primary, mb: 1.5 }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Box key={index} display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: entry.color,
                    border: '2px solid white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.secondary }}>
                  {entry.name}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 700, color: entry.color }}>
                {entry.value.toLocaleString()}
              </Typography>
            </Box>
          ))}
        </Card>
      );
    }
    return null;
  };

  const renderChart = () => {
    const chartData = getFilteredChartData();

    const commonProps = {
      data: chartData,
      margin: { top: 20, right: 60, left: 20, bottom: 20 }
    };

    switch (chartType) {
      case 'bar':
        return (
          <BarChart {...commonProps}>
            <defs>
              <linearGradient id="tiktokGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#00f2ea" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#00f2ea" stopOpacity={0.4} />
              </linearGradient>
              <linearGradient id="xhsGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ff6b9d" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#ff6b9d" stopOpacity={0.4} />
              </linearGradient>
              <linearGradient id="facebookGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#4267B2" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#4267B2" stopOpacity={0.4} />
              </linearGradient>
              <linearGradient id="instagramGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ff6b35" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#ff6b35" stopOpacity={0.4} />
              </linearGradient>
              {/* Individual platform gradients for thicker bars */}
              <linearGradient id="tiktokGradientThick" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#00f2ea" stopOpacity={0.9} />
                <stop offset="95%" stopColor="#00f2ea" stopOpacity={0.6} />
              </linearGradient>
              <linearGradient id="xhsGradientThick" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ff6b9d" stopOpacity={0.9} />
                <stop offset="95%" stopColor="#ff6b9d" stopOpacity={0.6} />
              </linearGradient>
              <linearGradient id="facebookGradientThick" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#4267B2" stopOpacity={0.9} />
                <stop offset="95%" stopColor="#4267B2" stopOpacity={0.6} />
              </linearGradient>
              <linearGradient id="instagramGradientThick" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ff6b35" stopOpacity={0.9} />
                <stop offset="95%" stopColor="#ff6b35" stopOpacity={0.6} />
              </linearGradient>
              <linearGradient id="totalGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.95} />
                <stop offset="50%" stopColor="#1d4ed8" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.6} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" opacity={0.6} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12, fill: theme.palette.text.secondary, fontWeight: 500 }}
              axisLine={{ stroke: '#e0e0e0', strokeWidth: 1 }}
              tickLine={false}
              tickMargin={10}
            />
            <YAxis
              tick={{ fontSize: 12, fill: theme.palette.text.secondary, fontWeight: 500 }}
              axisLine={{ stroke: '#e0e0e0', strokeWidth: 1 }}
              tickLine={false}
              tickMargin={10}
              allowDecimals={false}
              ticks={getYAxisTicks()}
              interval={0}
            />
            <Tooltip content={<CustomTooltip />} />
            {selectedPlatform === 'all' ? (
              <>
                <Bar
                  dataKey="total"
                  fill="url(#totalGradient)"
                  radius={[8, 8, 0, 0]}
                  animationDuration={1500}
                  animationBegin={0}
                  barSize={60}
                />
              </>
            ) : (
              <Bar
                dataKey={selectedPlatform}
                fill={`url(#${selectedPlatform}GradientThick)`}
                radius={[8, 8, 0, 0]}
                animationDuration={1500}
                animationBegin={0}
                barSize={60}
              />
            )}
          </BarChart>
        );

      case 'line':
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" opacity={0.6} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12, fill: theme.palette.text.secondary, fontWeight: 500 }}
              axisLine={{ stroke: '#e0e0e0', strokeWidth: 1 }}
              tickLine={false}
              tickMargin={10}
            />
            <YAxis
              tick={{ fontSize: 12, fill: theme.palette.text.secondary, fontWeight: 500 }}
              axisLine={{ stroke: '#e0e0e0', strokeWidth: 1 }}
              tickLine={false}
              tickMargin={10}
              allowDecimals={false}
              ticks={getYAxisTicks()}
              interval={0}
            />
            <Tooltip content={<CustomTooltip />} />
            {selectedPlatform === 'all' ? (
              <>
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke="#3b82f6"
                  strokeWidth={4}
                  dot={{ fill: '#3b82f6', stroke: '#ffffff', strokeWidth: 2, r: 6 }}
                  activeDot={{ r: 10, stroke: '#3b82f6', strokeWidth: 3, fill: '#ffffff' }}
                  animationDuration={2000}
                  animationBegin={0}
                />
              </>
            ) : (
              <>
                <Line
                  type="monotone"
                  dataKey="tiktok"
                  stroke="#00f2ea"
                  strokeWidth={3}
                  dot={{ fill: '#00f2ea', stroke: '#ffffff', strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 8, stroke: '#00f2ea', strokeWidth: 3, fill: '#ffffff' }}
                  animationDuration={2000}
                  animationBegin={0}
                />
                <Line
                  type="monotone"
                  dataKey="xhs"
                  stroke="#ff6b9d"
                  strokeWidth={3}
                  dot={{ fill: '#ff6b9d', stroke: '#ffffff', strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 8, stroke: '#ff6b9d', strokeWidth: 3, fill: '#ffffff' }}
                  animationDuration={2000}
                  animationBegin={300}
                />
                <Line
                  type="monotone"
                  dataKey="facebook"
                  stroke="#4267B2"
                  strokeWidth={3}
                  dot={{ fill: '#4267B2', stroke: '#ffffff', strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 8, stroke: '#4267B2', strokeWidth: 3, fill: '#ffffff' }}
                  animationDuration={2000}
                  animationBegin={600}
                />
                <Line
                  type="monotone"
                  dataKey="instagram"
                  stroke="#ff6b35"
                  strokeWidth={3}
                  dot={{ fill: '#ff6b35', stroke: '#ffffff', strokeWidth: 2, r: 5 }}
                  activeDot={{ r: 8, stroke: '#ff6b35', strokeWidth: 3, fill: '#ffffff' }}
                  animationDuration={2000}
                  animationBegin={900}
                />
              </>
            )}
          </LineChart>
        );

      case 'area':
        return (
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id="areaTiktok" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#00f2ea" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#00f2ea" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="areaXhs" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ff6b9d" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#ff6b9d" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="areaFacebook" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#4267B2" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#4267B2" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="areaInstagram" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ff6b35" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#ff6b35" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="areaTotal" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" opacity={0.6} />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12, fill: theme.palette.text.secondary, fontWeight: 500 }}
              axisLine={{ stroke: '#e0e0e0', strokeWidth: 1 }}
              tickLine={false}
              tickMargin={10}
            />
            <YAxis
              tick={{ fontSize: 12, fill: theme.palette.text.secondary, fontWeight: 500 }}
              axisLine={{ stroke: '#e0e0e0', strokeWidth: 1 }}
              tickLine={false}
              tickMargin={10}
              allowDecimals={false}
              ticks={getYAxisTicks()}
              interval={0}
            />
            <Tooltip content={<CustomTooltip />} />
            {selectedPlatform === 'all' ? (
              <Area
                type="monotone"
                dataKey="total"
                stroke="#3b82f6"
                fill="url(#areaTotal)"
                strokeWidth={3}
                animationDuration={2000}
                animationBegin={0}
              />
            ) : (
              <>
                <Area
                  type="monotone"
                  dataKey="tiktok"
                  stackId="1"
                  stroke="#3498db"
                  fill="url(#areaTiktok)"
                  strokeWidth={2}
                  animationDuration={2000}
                  animationBegin={0}
                />
                <Area
                  type="monotone"
                  dataKey="xhs"
                  stackId="1"
                  stroke="#e74c3c"
                  fill="url(#areaXhs)"
                  strokeWidth={2}
                  animationDuration={2000}
                  animationBegin={300}
                />
                <Area
                  type="monotone"
                  dataKey="facebook"
                  stackId="1"
                  stroke="#2ecc71"
                  fill="url(#areaFacebook)"
                  strokeWidth={2}
                  animationDuration={2000}
                  animationBegin={600}
                />
                <Area
                  type="monotone"
                  dataKey="instagram"
                  stackId="1"
                  stroke="#f39c12"
                  fill="url(#areaInstagram)"
                  strokeWidth={2}
                  animationDuration={2000}
                  animationBegin={900}
                />
              </>
            )}
          </AreaChart>
        );

      case 'pie':
        const pieData = [
          { name: 'TikTok', value: data.reduce((sum, item) => sum + item.tiktok, 0), color: '#00f2ea' },
          { name: 'XHS', value: data.reduce((sum, item) => sum + item.xhs, 0), color: '#ff6b9d' },
          { name: 'Facebook', value: data.reduce((sum, item) => sum + item.facebook, 0), color: '#4267B2' },
          { name: 'Instagram', value: data.reduce((sum, item) => sum + item.instagram, 0), color: '#ff6b35' }
        ];

        return (
          <PieChart {...commonProps}>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              outerRadius={120}
              fill="#8884d8"
              dataKey="value"
              animationDuration={2000}
              animationBegin={0}
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Card sx={{
          borderRadius: 8,
          boxShadow: '0 25px 80px rgba(0,0,0,0.08), 0 10px 30px rgba(0,0,0,0.04)',
          background: 'linear-gradient(135deg, #fafbfc 0%, #f1f5f9 50%, #e2e8f0 100%)',
          border: '1px solid rgba(148, 163, 184, 0.12)',
          overflow: 'hidden'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ mb: 4 }}>
              <Skeleton variant="text" width="60%" height={40} sx={{ mb: 2 }} />
              <Skeleton variant="text" width="40%" height={24} />
            </Box>

            <Grid container spacing={3} sx={{ mb: 4 }}>
              {[1, 2, 3, 4].map((item) => (
                <Grid item xs={12} sm={6} lg={3} key={item}>
                  <Skeleton
                    variant="rectangular"
                    height={160}
                    sx={{
                      borderRadius: 3,
                      background: 'linear-gradient(90deg, #f8fafc 25%, #e2e8f0 50%, #f8fafc 75%)',
                      backgroundSize: '200% 100%',
                      animation: 'shimmer 1.5s ease-in-out infinite',
                      '@keyframes shimmer': {
                        '0%': { backgroundPosition: '200% 0' },
                        '50%': { backgroundPosition: '-200% 0' },
                        '100%': { backgroundPosition: '200% 0' }
                      }
                    }}
                  />
                </Grid>
              ))}
            </Grid>

            <Skeleton
              variant="rectangular"
              height={500}
              sx={{
                borderRadius: 2,
                background: 'linear-gradient(90deg, #f8fafc 25%, #e2e8f0 50%, #f8fafc 75%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.5s ease-in-out infinite',
                '@keyframes shimmer': {
                  '0%': { backgroundPosition: '200% 0' },
                  '50%': { backgroundPosition: '-200% 0' },
                  '100%': { backgroundPosition: '200% 0' }
                }
              }}
            />
          </CardContent>
        </Card>
      </Box>
    );
  }

  const formatDateForQuery = (value) => {
    if (!value) {
      return null;
    }

    const dateObj = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(dateObj.getTime())) {
      return null;
    }

    return dateObj.toLocaleDateString('en-CA', { timeZone: 'Asia/Kuala_Lumpur' });
  };

  const formatDateTime = (value) => {
    if (!value) {
      return 'No activity recorded';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const timeAgo = (value) => {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
    const units = [
      { limit: 60, value: 1, suffix: 's' },
      { limit: 3600, value: 60, suffix: 'm' },
      { limit: 86400, value: 3600, suffix: 'h' },
      { limit: 604800, value: 86400, suffix: 'd' },
      { limit: 2592000, value: 604800, suffix: 'w' },
      { limit: Number.MAX_SAFE_INTEGER, value: 2592000, suffix: 'mo' }
    ];
    for (const u of units) {
      if (diffSec < u.limit) {
        const n = Math.max(1, Math.floor(diffSec / u.value));
        return `${n}${u.suffix} ago`;
      }
    }
    return '';
  };

  const handlePlatformClick = async (platform) => {
    if (!platform) {
      return;
    }

    const platformKey = platform.key || platform.title?.toLowerCase();
    if (!platformKey) {
      return;
    }

    setSelectedPlatformDetail(platform);
    setDetailEvents([]);
    setDetailError(null);
    setDetailModalOpen(true);
    setDetailLoading(true);

    try {
      const params = new URLSearchParams();
      params.append('platform', platformKey);

      const startDateStr = formatDateForQuery(startDate);
      const endDateStr = formatDateForQuery(endDate);

      if (startDateStr) {
        params.append('startDate', startDateStr);
      }
      if (endDateStr) {
        params.append('endDate', endDateStr);
      }

      const brand = getCurrentBrand();
      if (brand) {
        params.append('brand', brand);
      }

      const response = await fetch(`${API_ENDPOINTS.ANALYTICS_PLATFORM_DETAILS}?${params.toString()}`);
      if (!response.ok) {
        throw new Error(`Failed to load platform activity (${response.status})`);
      }

      const activityData = await response.json();
      setDetailEvents(Array.isArray(activityData) ? activityData : []);
    } catch (error) {
      console.error('Error fetching platform activity details:', error);
      setDetailError('Unable to load activity records right now.');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedPlatformDetail(null);
    setDetailEvents([]);
    setDetailError(null);
  };

  const renderPlatformIcon = (platformIcon) => {
    if (!platformIcon) {
      return null;
    }
    if (platformIcon === TikTokIcon || platformIcon === XHSIcon) {
      const IconComponent = platformIcon;
      return <IconComponent />;
    }
    const IconComponent = platformIcon;
    return <IconComponent sx={{ fontSize: 32, color: 'white' }} />;
  };

  const totalRecords = detailEvents.length;
  const latestEventDisplay = totalRecords > 0 ? formatDateTime(detailEvents[0].action_time) : 'No records yet';

  return (
    <>
      <Fade in timeout={800}>
        <Card sx={{
          borderRadius: { xs: 4, sm: 6, md: 8 },
          boxShadow: '0 25px 80px rgba(0,0,0,0.08), 0 10px 30px rgba(0,0,0,0.04), 0 0 0 1px rgba(255,255,255,0.1), inset 0 1px 0 rgba(255,255,255,0.2)',
          background: 'linear-gradient(135deg, #fafbfc 0%, #f1f5f9 50%, #e2e8f0 100%)',
          border: '1px solid rgba(148, 163, 184, 0.12)',
          overflow: 'hidden',
          width: '100%',
          position: 'relative',
          mb: { xs: 2, sm: 3, md: 4 },
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 32px 100px rgba(0,0,0,0.12), 0 16px 40px rgba(0,0,0,0.06), 0 0 0 1px rgba(255,255,255,0.15), inset 0 1px 0 rgba(255,255,255,0.3)',
            transform: { xs: 'none', sm: 'translateY(-4px)' },
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.03) 100%)',
            borderRadius: 'inherit',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '-1px',
            left: '-1px',
            right: '-1px',
            bottom: '-1px',
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1), rgba(16, 185, 129, 0.1))',
            borderRadius: 'inherit',
            zIndex: -1,
            opacity: 0,
            transition: 'opacity 0.4s ease',
          },
          '&:hover::after': {
            opacity: 1,
          },
        }}>
          <CardContent sx={{ p: 0 }}>
            {/* Date Range Picker Section */}
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{
                p: { xs: 2, sm: 3, md: 4 },
                pb: { xs: 2, sm: 2.5, md: 3 },
                background: 'linear-gradient(135deg, #7eb7dd 0%, #5a9fd4 50%, #3b82f6 100%)',
                borderTopLeftRadius: { xs: 8, sm: 12, md: 16 },
                borderTopRightRadius: { xs: 8, sm: 12, md: 16 },
                borderBottom: '1px solid rgba(148, 163, 184, 0.15)',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.03) 100%)',
                  borderRadius: 'inherit',
                  pointerEvents: 'none',
                },

              }}>
                <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'flex-start' }} gap={3}>
                  <Box sx={{ flex: '1 1 auto', minWidth: { xs: '100%', md: 280 } }}>
                    <Typography variant="h6" sx={{
                      fontWeight: 800,
                      color: '#0f172a',
                      mb: 1.5,
                      fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.3rem' },
                      letterSpacing: '-0.02em',
                      textShadow: '0 2px 4px rgba(255,255,255,0.4)',
                      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
                    }}>
                      {t('dashboard.dateRange')}
                    </Typography>
                    <Typography variant="body2" sx={{
                      color: '#334155',
                      lineHeight: 1.6,
                      fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' },
                      fontWeight: 600,
                      textShadow: '0 1px 2px rgba(255,255,255,0.3)',
                      opacity: 0.9
                    }}>
                      {t('dashboard.dateRangeDesc')}
                    </Typography>
                  </Box>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'flex-end' }} flexWrap="wrap" gap={2} sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
                    <Box sx={{ minWidth: { xs: '100%', sm: 140 } }}>
                      <DatePicker
                        label={t('dashboard.startDate')}
                        value={tempStartDate}
                        format="dd/MM/yyyy"
                        onChange={(newValue) => {
                          setTempStartDate(newValue);
                        }}
                        textField={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                backdropFilter: 'blur(8px)',
                                borderRadius: 2,
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                                border: '1px solid rgba(148, 163, 184, 0.15)',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 1)',
                                  borderColor: 'rgba(148, 163, 184, 0.25)',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                                },
                                '&.Mui-focused': {
                                  backgroundColor: 'rgba(255, 255, 255, 1)',
                                  borderColor: 'primary.main',
                                  boxShadow: '0 0 0 2px rgba(52, 152, 219, 0.1), 0 4px 12px rgba(0, 0, 0, 0.08)',
                                }
                              },
                              '& .MuiInputLabel-root': {
                                color: 'text.secondary',
                                fontWeight: 500,
                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                              }
                            }}
                          />
                        )}
                        maxDate={tempEndDate || new Date()}
                      />
                    </Box>

                    <Box sx={{ minWidth: { xs: '100%', sm: 140 } }}>
                      <DatePicker
                        label={t('dashboard.endDate')}
                        value={tempEndDate}
                        format="dd/MM/yyyy"
                        onChange={(newValue) => {
                          setTempEndDate(newValue);
                        }}
                        textField={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                backdropFilter: 'blur(8px)',
                                borderRadius: 2,
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                                border: '1px solid rgba(148, 163, 184, 0.15)',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 1)',
                                  borderColor: 'rgba(148, 163, 184, 0.25)',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                                },
                                '&.Mui-focused': {
                                  backgroundColor: 'rgba(255, 255, 255, 1)',
                                  borderColor: 'primary.main',
                                  boxShadow: '0 0 0 2px rgba(52, 152, 219, 0.1), 0 4px 12px rgba(0, 0, 0, 0.08)',
                                }
                              },
                              '& .MuiInputLabel-root': {
                                color: 'text.secondary',
                                fontWeight: 500,
                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                              }
                            }}
                          />
                        )}
                        minDate={tempStartDate}
                        maxDate={new Date()}
                      />
                    </Box>

                    <Button
                      variant="contained"
                      startIcon={<SearchIcon />}
                      onClick={handleSearch}
                      disabled={isLoading || !tempStartDate || !tempEndDate}
                      fullWidth={false}
                      sx={{
                        background: GRADIENTS.primary,
                        color: 'white',
                        fontWeight: 700,
                        fontSize: { xs: '0.8rem', sm: '0.875rem' },
                        px: { xs: 3, sm: 4 },
                        py: { xs: 1.25, sm: 1.5 },
                        borderRadius: 2.5,
                        boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4), 0 4px 12px rgba(29, 78, 216, 0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
                        border: 'none',
                        textTransform: 'none',
                        letterSpacing: '0.02em',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        minWidth: { xs: '100%', sm: 120 },
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                          transition: 'left 0.6s ease',
                        },
                        '&:hover': {
                          background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                          transform: 'translateY(-3px)',
                          boxShadow: '0 10px 30px rgba(59, 130, 246, 0.5), 0 6px 20px rgba(29, 78, 216, 0.4), inset 0 1px 0 rgba(255,255,255,0.3)',
                          '&::before': {
                            left: '100%',
                          },
                        },
                        '&:active': {
                          transform: 'translateY(-1px)',
                        },
                        '&.Mui-disabled': {
                          background: 'rgba(148, 163, 184, 0.2)',
                          color: 'rgba(148, 163, 184, 0.6)',
                          boxShadow: 'none',
                          transform: 'none',
                        }
                      }}
                    >
                      {isLoading ? t('dashboard.loading') : t('dashboard.search')}
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </LocalizationProvider>

            {/* Header Section */}
            <Box sx={{
              p: { xs: 2, sm: 3, md: 4 },
              pb: { xs: 1.5, sm: 2 },
              background: 'transparent',
              borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
              width: '100%'
            }}>
              <Box sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
                <Typography variant="h4" sx={{
                  fontWeight: 900,
                  color: '#0f172a',
                  mb: { xs: 1.5, sm: 2, md: 2.5 },
                  fontSize: { xs: '1.4rem', sm: '1.7rem', md: '2rem', lg: '2.25rem' },
                  letterSpacing: '-0.03em',
                  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                  textShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }}>
                  {t('dashboard.platformPerformance')}
                </Typography>
                <Typography variant="body1" sx={{
                  color: '#475569',
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  lineHeight: 1.7,
                  mb: { xs: 2.5, sm: 3, md: 3.5 },
                  fontWeight: 500,
                  letterSpacing: '0.01em',
                  opacity: 0.9
                }}>
                  {t('dashboard.platformPerformanceDesc')}
                </Typography>
              </Box>

              {/* Platform Stats Cards */}
              <Grid container spacing={{ xs: 2, sm: 2, md: 3, lg: 3 }} sx={{
                mt: 2,
                mb: 2,
                justifyContent: 'space-between',
                // Ensure proper card spacing and alignment
                '& .MuiGrid-item': {
                  display: 'flex',
                  alignItems: 'stretch',
                  minHeight: { xs: 'auto', sm: 200, md: 220, lg: 240 }
                },
                // Ensure cards maintain consistent heights on different screen sizes
                '& .platform-card': {
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: { xs: 200, sm: 200, md: 220, lg: 240 }
                },
                // Force horizontal layout on tablet and large screens
                '@media (min-width: 600px) and (max-width: 1280px)': {
                  display: 'block !important',
                  '&::after': {
                    content: '""',
                    display: 'table',
                    clear: 'both'
                  }
                }
              }}>
                {platformData.map((platform, index) => (
                  <Grid item xs={12} sm={6} md={6} lg={6} xl={2} key={index} sx={{
                    display: 'flex',
                    // Force horizontal layout on tablet and large screens
                    '@media (min-width: 600px) and (max-width: 1280px)': {
                      maxWidth: '50% !important',
                      flexBasis: '50% !important',
                      flexShrink: 0,
                      width: '50% !important',
                      float: 'left !important',
                      clear: 'none !important'
                    }
                  }}>
                    <Card
                      className="platform-card"
                      onClick={() => handlePlatformClick(platform)}
                      sx={{
                        height: '100%',
                        width: '100%',
                        flex: 1,
                        minWidth: { xs: 'auto', sm: 200, md: 220, lg: 240, xl: 300 },
                        background: platform.gradient,
                        color: 'white',
                        position: 'relative',
                        overflow: 'visible',
                        transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'pointer',
                        boxShadow: `${platform.shadow}, 0 6px 20px rgba(0,0,0,0.1), 0 0 0 1px rgba(255,255,255,0.1)`,
                        border: '1px solid rgba(255,255,255,0.15)',
                        backdropFilter: 'blur(20px)',
                        borderRadius: 3,
                        '&:hover': {
                          transform: { xs: 'none', sm: 'translateY(-16px) scale(1.05)' },
                          boxShadow: `${platform.shadow}, 0 32px 80px rgba(0,0,0,0.3), 0 16px 40px rgba(0,0,0,0.2), 0 0 0 1px ${platform.accent}40`,
                          '& .platform-icon': {
                            transform: { xs: 'none', sm: 'scale(1.25) rotate(15deg)' },
                            boxShadow: `${platform.glow}, 0 16px 50px rgba(0,0,0,0.4), inset 0 2px 0 rgba(255,255,255,0.4)`,
                          },
                          '& .platform-number': {
                            transform: { xs: 'none', sm: 'scale(1.1)' },
                            textShadow: `0 8px 25px rgba(0,0,0,0.7), 0 0 15px ${platform.accent}50`,
                          },
                          '& .platform-title': {
                            transform: { xs: 'none', sm: 'translateX(8px)' },
                            textShadow: `0 3px 10px rgba(0,0,0,0.4), 0 0 8px ${platform.accent}40`,
                          },
                          '& .platform-progress': {
                            '& .MuiLinearProgress-bar': {
                              boxShadow: `0 0 30px rgba(255,255,255,0.9), 0 8px 16px rgba(0,0,0,0.3), 0 0 15px ${platform.accent}50`,
                            },
                          },
                          '& .growth-chip': {
                            transform: { xs: 'none', sm: 'scale(1.15)' },
                            boxShadow: `0 6px 20px rgba(255,255,255,0.5), 0 0 15px ${platform.accent}40`,
                          },
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: `linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.03) 100%)`,
                          borderRadius: 'inherit',
                          opacity: 0,
                          transition: 'opacity 0.5s ease',
                        },
                        '&:hover::before': {
                          opacity: 1,
                        },
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: '-2px',
                          left: '-2px',
                          right: '-2px',
                          bottom: '-2px',
                          background: `linear-gradient(135deg, ${platform.accent}20, transparent, ${platform.accent}20)`,
                          borderRadius: 'inherit',
                          opacity: 0,
                          transition: 'opacity 0.5s ease',
                          zIndex: -1,
                        },
                        '&:hover::after': {
                          opacity: 1,
                        },
                      }}
                    >
                      <CardContent sx={{
                        p: { xs: 2, sm: 3 },
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        position: 'relative',
                        zIndex: 1,
                        minHeight: { xs: 'auto', sm: 180, md: 200, lg: 220 }
                      }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ flex: 1 }}>
                          <Box sx={{ flex: 1, minWidth: 0, pr: { xs: 1, sm: 1.5 } }}>
                            <Typography
                              className="platform-number"
                              variant="h3"
                              sx={{
                                fontWeight: 900,
                                mb: 1.5,
                                fontSize: { xs: '1.4rem', sm: '1.7rem', md: '2rem', lg: '2.4rem' },
                                textShadow: '0 3px 12px rgba(0,0,0,0.4)',
                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                letterSpacing: '-0.03em',
                                color: 'white',
                                lineHeight: 1.1,
                                wordBreak: 'break-word',
                                fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
                              }}
                              onClick={() => handlePlatformClick(platform)}
                            >
                              {Math.round(platform.value).toLocaleString()}
                            </Typography>
                            <Typography
                              className="platform-title"
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                mb: 1.5,
                                fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1.1rem', lg: '1.3rem' },
                                textShadow: '0 2px 6px rgba(0,0,0,0.3)',
                                letterSpacing: '0.03em',
                                transition: 'transform 0.3s ease',
                                color: 'rgba(255,255,255,0.98)',
                                lineHeight: 1.3,
                                wordBreak: 'break-word',
                                fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
                              }}
                            >
                              {platform.title}
                            </Typography>
                            <Chip
                              className="growth-chip"
                              label={platform.growth}
                              size="small"
                              sx={{
                                background: 'rgba(255,255,255,0.25)',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.3)',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  background: 'rgba(255,255,255,0.35)',
                                  transform: 'scale(1.05)',
                                }
                              }}
                            />
                          </Box>
                          <Box
                            className="platform-icon"
                            sx={{
                              width: { xs: 40, sm: 50, md: 60, lg: 70 },
                              height: { xs: 40, sm: 50, md: 60, lg: 70 },
                              borderRadius: '50%',
                              background: 'rgba(255,255,255,0.2)',
                              backdropFilter: 'blur(20px)',
                              border: '2px solid rgba(255,255,255,0.3)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                              boxShadow: '0 8px 32px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.2)',
                              position: 'relative',
                              flexShrink: 0,
                              ml: { xs: 1, sm: 1.5, md: 2 },
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                top: '-2px',
                                left: '-2px',
                                right: '-2px',
                                bottom: '-2px',
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 100%)',
                                zIndex: -1,
                                opacity: 0,
                                transition: 'opacity 0.3s ease',
                              },
                              '&:hover::after': {
                                opacity: 1,
                              },
                            }}
                          >
                            {platform.icon === TikTokIcon ? (
                              <platform.icon />
                            ) : platform.icon === XHSIcon ? (
                              <platform.icon />
                            ) : (
                              <platform.icon sx={{ fontSize: { xs: 24, sm: 28, md: 32 }, color: 'white' }} />
                            )}
                          </Box>
                        </Box>

                        <Box sx={{ mt: 2, flexShrink: 0 }}>
                          <LinearProgress
                            className="platform-progress"
                            variant="determinate"
                            value={getPlatformPercentage(platform.value)}
                            sx={{
                              height: { xs: 6, sm: 8 },
                              borderRadius: { xs: 3, sm: 4 },
                              backgroundColor: 'rgba(255,255,255,0.15)',
                              mb: 1.5,
                              '& .MuiLinearProgress-bar': {
                                background: 'linear-gradient(90deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0.6) 100%)',
                                borderRadius: { xs: 3, sm: 4 },
                                boxShadow: '0 0 16px rgba(255,255,255,0.5), 0 2px 8px rgba(0,0,0,0.15)',
                                transition: 'all 0.3s ease',
                              },
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'rgba(255,255,255,0.95)',
                              fontSize: { xs: '0.7rem', sm: '0.8rem' },
                              fontWeight: 600,
                              textShadow: '0 1px 3px rgba(0,0,0,0.2)',
                              letterSpacing: '0.03em',
                              textAlign: 'left',
                              fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
                            }}
                          >
                            {getPlatformPercentage(platform.value).toFixed(1)}% {t('dashboard.ofTotal')}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Control Panel Row - Below Platform Cards */}
              <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', lg: 'center' },
                flexWrap: 'wrap',
                gap: { xs: 2, sm: 3 },
                p: { xs: 2, sm: 3 },
                mt: 3,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                }
              }}>
                {/* Platform Selector */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                  <Typography variant="body2" sx={{
                    color: '#374151',
                    fontWeight: 700,
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    opacity: 0.9,
                    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
                  }}>
                    {t('dashboard.selectPlatform')}
                  </Typography>
                  <ToggleButtonGroup
                    value={selectedPlatform}
                    exclusive
                    onChange={(e, newValue) => newValue && setSelectedPlatform(newValue)}
                    size="small"
                    sx={{
                      width: '100%',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      '& .MuiToggleButtonGroup-grouped': {
                        margin: 0,
                        border: 0,
                        '&:not(:first-of-type)': {
                          borderRadius: 2,
                          borderLeft: '2px solid rgba(148, 163, 184, 0.2)',
                        },
                        '&:first-of-type': {
                          borderRadius: 2,
                        },
                      },
                      '& .MuiToggleButton-root': {
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 1, sm: 1.25 },
                        border: '2px solid',
                        borderColor: 'rgba(148, 163, 184, 0.2)',
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: { xs: '0.75rem', sm: '0.8rem' },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        minWidth: { xs: 'calc(50% - 8px)', sm: 65 },
                        flexGrow: { xs: 1, sm: 0 },
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(15px)',
                        color: 'rgba(55, 65, 81, 0.9)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.1), transparent)',
                          transition: 'left 0.5s ease',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(59, 130, 246, 0.08)',
                          borderColor: 'rgba(59, 130, 246, 0.4)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 20px rgba(59, 130, 246, 0.2), 0 4px 12px rgba(29, 78, 216, 0.15)',
                          color: 'rgba(59, 130, 246, 0.9)',
                          '&::before': {
                            left: '100%',
                          },
                        },
                        '&.Mui-selected': {
                          background: GRADIENTS.primary,
                          color: 'white',
                          borderColor: '#3b82f6',
                          boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4), 0 4px 12px rgba(29, 78, 216, 0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(59, 130, 246, 0.5), 0 6px 16px rgba(29, 78, 216, 0.4), inset 0 1px 0 rgba(255,255,255,0.3)',
                          }
                        }
                      }
                    }}
                  >
                    <ToggleButton value="all" aria-label="all platforms">
                      {t('dashboard.all')}
                    </ToggleButton>
                    <ToggleButton value="tiktok" aria-label="tiktok">
                      {t('dashboard.platforms.tiktok')}
                    </ToggleButton>
                    <ToggleButton value="xhs" aria-label="xhs">
                      {t('dashboard.platforms.xhs')}
                    </ToggleButton>
                    <ToggleButton value="facebook" aria-label="facebook">
                      {t('dashboard.platforms.fb')}
                    </ToggleButton>
                    <ToggleButton value="instagram" aria-label="instagram">
                      {t('dashboard.platforms.ig')}
                    </ToggleButton>
                    <ToggleButton value="whatsapp" aria-label="whatsapp">
                      {t('dashboard.platforms.wa')}
                    </ToggleButton>
                    <ToggleButton value="google" aria-label="google">
                      {t('dashboard.platforms.google')}
                    </ToggleButton>
                    {(['connectai', 'ashley'].includes(String(getCurrentBrand() || '').toLowerCase())) && (
                      <ToggleButton value="lemon8" aria-label="lemon8">
                        {t('dashboard.platforms.lemon8')}
                      </ToggleButton>
                    )}
                  </ToggleButtonGroup>
                </Box>

                {/* Chart Type Toggle */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                  <Typography variant="body2" sx={{
                    color: '#374151',
                    fontWeight: 700,
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    opacity: 0.9,
                    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
                  }}>
                    {t('dashboard.chartType')}
                  </Typography>
                  <ToggleButtonGroup
                    value={chartType}
                    exclusive
                    onChange={(e, newType) => newType && setChartType(newType)}
                    size="medium"
                    sx={{
                      '& .MuiToggleButton-root': {
                        px: { xs: 2.5, sm: 3.5 },
                        py: { xs: 1.5, sm: 1.75 },
                        border: '2px solid',
                        borderColor: 'rgba(148, 163, 184, 0.2)',
                        borderRadius: 2.5,
                        fontWeight: 600,
                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(15px)',
                        color: 'rgba(55, 65, 81, 0.9)',
                        minWidth: { xs: 70, sm: 80 },
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.1), transparent)',
                          transition: 'left 0.5s ease',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(59, 130, 246, 0.08)',
                          borderColor: 'rgba(59, 130, 246, 0.4)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 20px rgba(59, 130, 246, 0.2), 0 4px 12px rgba(29, 78, 216, 0.15)',
                          color: 'rgba(59, 130, 246, 0.9)',
                          '&::before': {
                            left: '100%',
                          },
                        },
                        '&.Mui-selected': {
                          background: GRADIENTS.primary,
                          color: 'white',
                          borderColor: '#3b82f6',
                          boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4), 0 4px 12px rgba(29, 78, 216, 0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(59, 130, 246, 0.5), 0 6px 16px rgba(29, 78, 216, 0.4), inset 0 1px 0 rgba(255,255,255,0.3)',
                          },
                          '& .MuiSvgIcon-root': {
                            color: 'white',
                          }
                        },
                        '& .MuiSvgIcon-root': {
                          transition: 'all 0.3s ease',
                        }
                      }
                    }}
                  >
                    <ToggleButton value="bar" aria-label="bar chart">
                      <BarChartIcon sx={{ fontSize: { xs: 16, sm: 18 }, mr: 1, opacity: 0.9 }} />
                      {t('dashboard.bar')}
                    </ToggleButton>
                    <ToggleButton value="line" aria-label="line chart">
                      <LineChartIcon sx={{ fontSize: { xs: 16, sm: 18 }, mr: 1, opacity: 0.9 }} />
                      {t('dashboard.line')}
                    </ToggleButton>
                    <ToggleButton value="area" aria-label="area chart">
                      <AreaChartIcon sx={{ fontSize: { xs: 16, sm: 18 }, mr: 1, opacity: 0.9 }} />
                      {t('dashboard.area')}
                    </ToggleButton>
                    {selectedPlatform === 'all' && (
                      <ToggleButton value="pie" aria-label="pie chart">
                        <PieChartIcon sx={{ fontSize: { xs: 16, sm: 18 }, mr: 1, opacity: 0.9 }} />
                        {t('dashboard.pie')}
                      </ToggleButton>
                    )}
                  </ToggleButtonGroup>
                </Box>
              </Box>
            </Box>

            {/* Chart Section */}
            <Box sx={{
              p: { xs: 2, sm: 3, md: 4 },
              pt: { xs: 1, sm: 1.5, md: 2 },
              background: 'linear-gradient(180deg, rgba(248, 250, 252, 0.5) 0%, rgba(241, 245, 249, 0.3) 100%)',
              borderTop: '1px solid rgba(148, 163, 184, 0.08)',
              borderBottomLeftRadius: 50,
              borderBottomRightRadius: 50,
              position: 'relative',

              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '1px',
                background: 'linear-gradient(90deg, transparent, rgba(148, 163, 184, 0.2), transparent)',
              }
            }}>
              {selectedPlatform !== 'all' && (
                <Box sx={{
                  mb: 3,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: { xs: 1, sm: 2 },
                  p: { xs: 1.5, sm: 2 },
                  background: 'rgba(59, 130, 246, 0.05)',
                  borderRadius: 2,
                  border: '1px solid rgba(59, 130, 246, 0.1)',
                  backdropFilter: 'blur(10px)'
                }}>
                  <Typography variant="h6" sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: { xs: '1rem', sm: '1.25rem' }
                  }}>
                    {t('dashboard.showingDataFor')}
                  </Typography>
                  <Chip
                    label={platformData.find(p => p.title.toLowerCase().includes(selectedPlatform))?.title || selectedPlatform}
                    color="primary"
                    variant="filled"
                    sx={{
                      fontWeight: 600,
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                    }}
                  />
                  <Button
                    size="small"
                    onClick={() => setSelectedPlatform('all')}
                    sx={{
                      textTransform: 'none',
                      background: 'rgba(59, 130, 246, 0.1)',
                      color: '#3b82f6',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      '&:hover': {
                        background: 'rgba(59, 130, 246, 0.15)',
                        borderColor: 'rgba(59, 130, 246, 0.3)',
                      }
                    }}
                  >
                    {t('dashboard.showAllPlatforms')}
                  </Button>
                </Box>
              )}
              <Box sx={{
                height: { xs: 350, sm: 400, md: 500 },
                width: '100%',
                overflow: 'visible',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '1px',
                  background: 'linear-gradient(90deg, transparent, rgba(148, 163, 184, 0.2), transparent)',
                }
              }}>
                {(() => {
                  const chartData = getFilteredChartData();
                  const isInsufficientData = chartData.length < 3;
                  const isPieUnavailable = chartType === 'pie' && selectedPlatform !== 'all';

                  if (isInsufficientData) {
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          minHeight: '300px',
                          textAlign: 'center',
                          p: 4
                        }}
                      >
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 3,
                            border: '2px solid #dee2e6'
                          }}
                        >
                          <BarChartIcon sx={{ fontSize: 40, color: '#6c757d' }} />
                        </Box>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 600,
                            color: '#495057',
                            mb: 1,
                            fontSize: { xs: '1.25rem', sm: '1.5rem' }
                          }}
                        >
                          {t('dashboard.insufficientData')}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: '#6c757d',
                            maxWidth: '300px',
                            lineHeight: 1.6,
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                          }}
                        >
                          {t('dashboard.insufficientDataDesc')}
                        </Typography>
                      </Box>
                    );
                  }

                  if (isPieUnavailable) {
                    return (
                      <Box
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          p: 4
                        }}
                      >
                        <PieChartIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2, opacity: 0.5 }} />
                        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1, fontWeight: 600 }}>
                          {t('dashboard.pieChartUnavailable')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.disabled', maxWidth: 300 }}>
                          {t('dashboard.pieChartUnavailableDesc')}
                        </Typography>
                      </Box>
                    );
                  }

                  return (
                    <ResponsiveContainer width="100%" height="100%">
                      {renderChart()}
                    </ResponsiveContainer>
                  );
                })()}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Fade>

      <Dialog
        open={detailModalOpen}
        onClose={handleCloseDetailModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 4,
            boxShadow: '0 24px 80px rgba(15, 23, 42, 0.2)'
          }
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: { xs: 3, sm: 4 },
              py: 3,
              background: selectedPlatformDetail?.gradient || 'linear-gradient(120deg, #1d4ed8, #9333ea)',
              color: 'white',
              gap: 3
            }}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  boxShadow: '0 18px 40px rgba(15, 23, 42, 0.25)',
                  color: 'white'
                }}
              >
                {selectedPlatformDetail && renderPlatformIcon(selectedPlatformDetail.icon)}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>
                  {selectedPlatformDetail?.title || 'Platform Details'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  Activity timeline for the selected date range
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            background: 'linear-gradient(180deg, rgba(241, 245, 249, 0.9) 0%, rgba(226, 232, 240, 0.6) 100%)',
            px: { xs: 3, sm: 4 },
            py: { xs: 3, sm: 4 }
          }}
        >
          {detailLoading ? (
            <Box display="flex" alignItems="center" justifyContent="center" minHeight={180} py={4}>
              <CircularProgress />
            </Box>
          ) : detailError ? (
            <Alert severity="error">{detailError}</Alert>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2.5}
                sx={{ mb: 1.5 }}
              >
                <Box
                  sx={{
                    flex: 1,
                    p: 2.5,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(59,130,246,0.12), rgba(59,130,246,0.06))',
                    border: '1px solid rgba(59,130,246,0.18)',
                    boxShadow: '0 16px 40px rgba(30, 64, 175, 0.15)'
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2" sx={{ color: '#1e3a8a', fontWeight: 700 }}>
                      Total Records
                    </Typography>
                    <Avatar sx={{ bgcolor: 'rgba(59,130,246,0.2)', color: '#1e3a8a', width: 28, height: 28 }}>
                      <ListAltIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#1d4ed8' }}>
                    {totalRecords.toLocaleString()}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    p: 2.5,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(16,185,129,0.12), rgba(16,185,129,0.05))',
                    border: '1px solid rgba(16,185,129,0.18)',
                    boxShadow: '0 16px 40px rgba(6, 95, 70, 0.12)'
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2" sx={{ color: '#047857', fontWeight: 700 }}>
                      Latest Record
                    </Typography>
                    <Avatar sx={{ bgcolor: 'rgba(16,185,129,0.2)', color: '#047857', width: 28, height: 28 }}>
                      <AccessTimeIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#034d40' }}>
                    {latestEventDisplay}
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 1.5, borderColor: 'rgba(148, 163, 184, 0.3)' }} />

              {detailEvents.length > 0 ? (
                <List
                  dense
                  sx={{
                    maxHeight: 360,
                    overflowY: 'auto',
                    pr: 1,
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 24,
                      bottom: 24,
                      left: 32,
                      width: '2px',
                      background: 'linear-gradient(180deg, rgba(99,102,241,0.18) 0%, rgba(99,102,241,0) 100%)'
                    }
                  }}
                >
                  {detailEvents.map((event, index) => {
                    const eventTime = formatDateTime(event.action_time);
                    return (
                      <ListItem
                        key={`${event.action_time}-${index}`}
                        disableGutters
                        sx={{
                          alignItems: 'center',
                          py: 1.5,
                          pl: { xs: 5.5, sm: 6.5 },
                          pr: 2.5,
                          mb: 1.75,
                          borderRadius: 3,
                          background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(224,231,255,0.92) 100%)',
                          border: '1px solid rgba(99,102,241,0.15)',
                          boxShadow: 'none',
                          backdropFilter: 'blur(14px)',
                          position: 'relative',
                          '&:hover': {
                            background: 'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(224,231,255,0.98) 100%)'
                          },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 26,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, rgba(99,102,241,0.8), rgba(59,130,246,0.8))',
                            boxShadow: '0 4px 10px rgba(99,102,241,0.4)',
                            border: '2px solid #ffffff'
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 46, mr: 1.5 }}>
                          <Box
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, rgba(99,102,241,0.25), rgba(59,130,246,0.2))',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#4338ca',
                              boxShadow: '0 10px 22px rgba(99,102,241,0.25), inset 0 1px 0 rgba(255,255,255,0.35)',
                              border: '1px solid rgba(99,102,241,0.25)'
                            }}
                          >
                            <AccessTimeIcon sx={{ fontSize: 18 }} />
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primaryTypographyProps={{
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            color: '#312e81'
                          }}
                          primary={eventTime}
                          secondary={timeAgo(event.action_time)}
                          secondaryTypographyProps={{
                            fontSize: '0.8rem',
                            color: '#475569',
                            fontWeight: 600
                          }}
                        />
                        <Box
                          sx={{
                            ml: 2,
                            px: 1.75,
                            py: 0.5,
                            borderRadius: 999,
                            backgroundColor: 'rgba(79,70,229,0.18)',
                            color: '#312e81',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            letterSpacing: '0.03em',
                            textTransform: 'uppercase'
                          }}
                        >
                          #{detailEvents.length - index}
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No activity recorded for this platform in the selected date range.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 3, sm: 4 },
            py: 3,
            background: 'linear-gradient(180deg, rgba(148,163,184,0.1) 0%, rgba(148,163,184,0.2) 100%)',
            borderTop: '1px solid rgba(148, 163, 184, 0.2)'
          }}
        >
          <Button
            onClick={handleCloseDetailModal}
            variant="contained"
            sx={{
              background: selectedPlatformDetail?.gradient || 'linear-gradient(135deg, #1d4ed8, #9333ea)',
              fontWeight: 700,
              px: 3,
              py: 1.2,
              letterSpacing: '0.04em',
              textTransform: 'none',
              boxShadow: '0 18px 32px rgba(30, 58, 138, 0.35)',
              '&:hover': {
                background: selectedPlatformDetail?.gradient || 'linear-gradient(135deg, #1e3a8a, #7c3aed)',
                boxShadow: '0 22px 36px rgba(30, 58, 138, 0.35)'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DashboardOverview;
